import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import KpiCard from '../KpiCard.jsx';
import WindowPicker from './WindowPicker.jsx';

/**
 * Per-partner outcomes: what we asked each demand partner, what came back, and what failed.
 *
 * WHY THIS IS THE FIRST TAB
 * -------------------------
 * `/api/admin/stats/partner-outcomes` is the single most informative view this platform has, and
 * until now it had no reader. The 2026-09-30 read of it found, in one table, that Vidverto had
 * `circuit_open` on 2,683,353 of 2,687,350 calls -- 99.85% never left the building -- and that
 * Adokut answered 39,879 requests cleanly in 26ms and bid on none of them. Those are two
 * completely different problems (one ours, one theirs) and neither was visible anywhere in the
 * product.
 *
 * The failure breakdown is therefore NOT collapsed into a single "errors" number. `circuit_open`
 * means we declined to call; `timeout` means we called and they were too slow; `http_error` means
 * they answered badly. Summing them would hide exactly the distinction an operator needs.
 */

function pct(n) {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(4)}%`;
}

function int(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString();
}

export default function PartnerPerformanceTab() {
  const [days, setDays] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .partnerOutcomes(days)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load partner outcomes.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const partners = (data?.partners || []).slice().sort((a, b) => (b.calls || 0) - (a.calls || 0));
  const totalCalls = partners.reduce((s, p) => s + (p.calls || 0), 0);
  const totalBids = partners.reduce((s, p) => s + (p.bids || 0), 0);
  const totalWins = partners.reduce((s, p) => s + (p.wins || 0), 0);
  const bidders = partners.filter((p) => p.bids > 0).length;
  // "Silent" means a partner we called at real volume that returned no bid at all. A partner
  // called three times and bidding zero is noise; one called 112,559 times and bidding zero is a
  // commercial problem, and the two should not be counted together.
  const silent = partners.filter((p) => p.calls > 1000 && !p.bids).length;

  return (
    <div className="stack">
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Partner outcomes</h2>
          <WindowPicker days={days} onChange={setDays} disabled={loading} />
        </div>
        <p className="card__hint">
          What each enabled demand partner was asked, what came back, and how it failed when it did.
          A partner answering cleanly and bidding zero is a commercial conversation; a partner whose
          circuit breaker is open is an engineering one. The breakdown keeps those apart.
        </p>

        {loading && <LoadingState label="Loading partner outcomes" />}
        {error && !loading && <EmptyState title="Couldn't load partner outcomes" message={error} />}

        {!loading && !error && (
          <>
            <div className="kpi-grid" style={{ marginTop: '14px' }}>
              <KpiCard label="Calls" value={int(totalCalls)} />
              <KpiCard
                label="Bids"
                value={int(totalBids)}
                sub={totalCalls ? pct(totalBids / totalCalls) : undefined}
                tone="accent"
              />
              <KpiCard label="Wins" value={int(totalWins)} />
              <KpiCard
                label="Partners bidding"
                value={`${bidders} of ${partners.length}`}
                sub={silent ? `${silent} silent on real volume` : undefined}
              />
            </div>

            {partners.length === 0 ? (
              <EmptyState
                title="No partner activity"
                message="No demand partner was called in this window."
              />
            ) : (
              <div className="table-scroll" style={{ marginTop: '18px' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Partner</th>
                      <th className="num">Calls</th>
                      <th className="num">Responded</th>
                      <th className="num">Response rate</th>
                      <th className="num">Bids</th>
                      <th className="num">Bid rate</th>
                      <th className="num">Wins</th>
                      <th className="num">Circuit open</th>
                      <th className="num">Timeout</th>
                      <th className="num">HTTP error</th>
                      <th className="num">Mean ms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partners.map((p) => {
                      const c = p.counts || {};
                      return (
                        <tr key={p.code}>
                          <td className="cell-title">{p.code}</td>
                          <td className="num">{int(p.calls)}</td>
                          <td className="num">{int(p.responded)}</td>
                          <td className="num cell-muted">{pct(p.responseRate)}</td>
                          <td className="num">{int(p.bids)}</td>
                          <td className="num">{pct(p.bidRate)}</td>
                          <td className="num">{int(p.wins)}</td>
                          <td className="num cell-muted">{c.circuit_open ? int(c.circuit_open) : '—'}</td>
                          <td className="num cell-muted">{c.timeout ? int(c.timeout) : '—'}</td>
                          <td className="num cell-muted">{c.http_error ? int(c.http_error) : '—'}</td>
                          <td className="num cell-muted">{p.latencyMs?.meanMs ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {bidders === 1 && partners.length > 1 && (
              <p className="card__hint card__hint--warning" style={{ marginTop: '12px' }}>
                One partner is bidding. With a single bidder there is no price competition, so the
                clearing price is whatever that partner offers — adding supply will not change it.
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}
